import {
 get,
 patch,
 post,
 put,
 remove,
} from 'utils/axios/index'
import {toast} from 'react-toastify'
import ApiResponse, {
 DefaultApiResponse,
} from 'models/ApiResponse'
import {AxiosError} from 'axios'
import {
 handleRejectedErrorMsg,
 showErrorMsg,
} from 'utils/helpers/handleError'
import {
 IProductCategoryLicense,
 ILicenseDeleteRequest,
 IInfra,
 IInfraUpdateRequest,
 IInfraDeleteRequest,
} from 'models/MyAccount'
import {IOnboardingItems} from 'models/GenericForm'
import {IFormField} from 'models/Common'
import IDropdownVal from 'models/dropdown'

const fetchParentOnboardingItmesConfig = async (
 user_id?: string
) => {
 try {
  return await (
   await get
  )<DefaultApiResponse>(
   '/rb-platform/v1/onboarding-items',
   {
    ...(user_id && {user_id}),
   }
  ).then((res) => {
   if (res) {
    return res.data
   } else {
    toast.error('Something went wrong')
    return []
   }
  })
 } catch (err: unknown) {
  const e = err as AxiosError
  handleRejectedErrorMsg(e)
  return []
 }
}

const fetchChildOnboardingItmesConfig = async (
 parentId: number,
 user_id?: string
) => {
 try {
  return await (
   await get
  )<DefaultApiResponse>(
   `rb-platform/v1/onboarding-items/${parentId}/children`,
   {user_id}
  ).then((res) => {
   if (res?.data) {
    return res.data
   } else {
    toast.error('Something went wrong')
    return []
   }
  })
 } catch (err: unknown) {
  const e = err as AxiosError
  handleRejectedErrorMsg(e)
  return []
 }
}

const updateOnboardingItmes = async (
 data: IOnboardingItems
) => {
 try {
  const res = await patch<DefaultApiResponse>(
   '/rb-platform/v1/onboarding-items/update-fields/',
   {data}
  )
  if (res.success) {
   return res?.message
  } else {
   showErrorMsg(res.message)
   return null
  }
 } catch (err: unknown) {
  const e = err as AxiosError
  handleRejectedErrorMsg(e)
  return null
 }
}

const normalizeLicenseList = (
 data: unknown
): IProductCategoryLicense[] => {
 if (Array.isArray(data)) {
  return data
 }
 if (data && typeof data === 'object') {
  const nested = data as {
   results?: unknown
   data?: unknown
   licenses?: unknown
  }
  if (Array.isArray(nested.results)) return nested.results
  if (Array.isArray(nested.data)) return nested.data
  if (Array.isArray(nested.licenses)) return nested.licenses
 }
 return []
}

const fetchProductCategoryLicense = async (
 user_id?: string,
 business_service?: string,
 business_group?: string
) => {
 try {
  return await (
   await get
  )<DefaultApiResponse>(
   `/product/v1/product-business-group/license`,
   {
    ...(user_id && {user_id}),
    ...(business_group && {
     business_group,
    }),
    ...(business_service && {business_service}),
   }
  ).then((res) => {
   if (res) {
    return normalizeLicenseList(res.data)
   } else {
    toast.error('Something went wrong')
    return []
   }
  })
 } catch (err: unknown) {
  const e = err as AxiosError
  handleRejectedErrorMsg(e)
  return []
 }
}

const addProductCategoryLicense = async (
 license_data: IProductCategoryLicense[],
 user_id?: string
) => {
 try {
  const res = await post<DefaultApiResponse>(
   `/product/v1/product-business-group/license`,
   {license_data, user_id}
  )
  if (res.success) {
   return res
  } else {
   showErrorMsg(res.message)
   return null
  }
 } catch (err: unknown) {
  const e = err as AxiosError
  handleRejectedErrorMsg(e)
  return null
 }
}

const updateProductCategoryLicense = async (
 license_data: IProductCategoryLicense[],
 user_id?: string
) => {
 try {
  const res = await put<DefaultApiResponse>(
   `/product/v1/product-business-group/license/update`,
   {
    ...(user_id && {user_id}),
    license_data,
   }
  )
  if (res.success) {
   return res
  } else {
   showErrorMsg(res.message)
   return null
  }
 } catch (err: unknown) {
  const e = err as AxiosError
  handleRejectedErrorMsg(e)
  return null
 }
}

const deleteProductCategoryLicense = async (
 payload: ILicenseDeleteRequest
) => {
 try {
  const res = await remove<DefaultApiResponse>(
   `/product/v1/product-business-group/license/update`,
   {...payload}
  )
  if (res?.success) {
   toast.success(
    res.message ?? 'License deleted successfully'
   )
   return res
  }
  showErrorMsg(res.message)
  return null
 } catch (err: unknown) {
  const e = err as AxiosError
  handleRejectedErrorMsg(e)
  return null
 }
}

const fetchProductCategory = async () => {
 try {
  return await get<DefaultApiResponse>(
   '/product/business-group'
  ).then((res) => {
   if (res.success) {
    return res.data
   } else {
    toast.error(res.message)
    return null
   }
  })
 } catch (err: unknown) {
  const e = err as AxiosError
  handleRejectedErrorMsg(e)
  return []
 }
}

const fetchOrganisationMapping = async (
 user_id?: string,
 page?: number
) => {
 try {
  return await (
   await get
  )<DefaultApiResponse>(`/rb/v1/organisation/mappings`, {
   ...(user_id && {user_id}),
   ...(page && {page}),
  }).then((res) => {
   if (res) {
    return res
   } else {
    toast.error('Something went wrong')
    return null
   }
  })
 } catch (err: unknown) {
  const e = err as AxiosError
  handleRejectedErrorMsg(e)
  return null
 }
}
const fetchOrganisation = async (
 category?: number,
 stateIds?: number[],
 districtIds?: number[]
) => {
 try {
  return await get<ApiResponse<IDropdownVal>>(
   `/rb/v1/organisations`,
   {
    ...(category && {category}),
    ...(stateIds &&
     stateIds.length > 0 && {state_id: stateIds.join(',')}),
    ...(districtIds &&
     districtIds.length > 0 && {
      district_id: districtIds.join(','),
     }),
   }
  ).then((res) => {
   if (res) {
    return res?.data
   } else {
    toast.error('Something went wrong')
    return []
   }
  })
 } catch (err: unknown) {
  const e = err as AxiosError
  handleRejectedErrorMsg(e)
  return []
 }
}

const createOrganisationMapping = async (
 payload: IFormField
) => {
 try {
  return await post<DefaultApiResponse>(
   `/rb/v1/organisation/mappings`,
   payload
  ).then((res) => {
   if (res?.success) {
    return res.message
   } else {
    toast.error('Something went wrong')
    return null
   }
  })
 } catch (err: unknown) {
  const e = err as AxiosError
  handleRejectedErrorMsg(e)
  return null
 }
}

const removeOrganisationMapping = async (
 payload: IFormField
) => {
 try {
  return await remove<DefaultApiResponse>(
   `/rb/v1/organisation/mappings`,
   payload
  ).then((res) => {
   if (res?.success) {
    return res.message
   } else {
    toast.error('Something went wrong')
    return null
   }
  })
 } catch (err: unknown) {
  const e = err as AxiosError
  handleRejectedErrorMsg(e)
  return null
 }
}

const fetchOrganisationMappingCounts = async (
 user_id?: string
) => {
 try {
  return await (
   await get
  )<DefaultApiResponse>(
   `/rb/v1/organisation/mapping-counts`,
   {
    ...(user_id && {user_id}),
   }
  ).then((res) => {
   if (res.success) {
    return res.data
   } else {
    toast.error('Something went wrong')
    return []
   }
  })
 } catch (err: unknown) {
  const e = err as AxiosError
  handleRejectedErrorMsg(e)
  return []
 }
}

const fetchInfraList = async (user_id?: string) => {
 try {
  const res = await (
   await get
  )<DefaultApiResponse | IInfra[]>(
   `/market-linkage/v1/infra`,
   {
    ...(user_id && {user_id}),
   }
  )

  if (Array.isArray(res) && res.length > 0) {
   return res
  }
  if (
   !Array.isArray(res) &&
   Array.isArray(res.data) &&
   res.data.length > 0
  ) {
   return res.data as IInfra[]
  }
  return []
 } catch (err: unknown) {
  const e = err as AxiosError
  handleRejectedErrorMsg(e)
  return []
 }
}

const fetchInfraTypes = async () => {
 try {
  const res = await get<DefaultApiResponse>(
   '/market-linkage/v1/infra/types'
  )

  const rawList = Array.isArray(res)
   ? res
   : Array.isArray(res?.data)
   ? res.data
   : []

  return rawList
   .map(
    (item: {
     id?: string | number
     name?: string
     value?: string | number
     label?: string
     is_active?: boolean
    }) => ({
     id: item.value ?? item.id,
     name: item.label ?? item.name,
     is_active: item.is_active ?? true,
    })
   )
   .filter(
    (
     item
    ): item is {
     id: string | number
     name: string
     is_active: boolean
    } => item.id !== undefined && item.name !== undefined
   )
 } catch (err: unknown) {
  const e = err as AxiosError
  handleRejectedErrorMsg(e)
  return []
 }
}

const createInfra = async (
 data: IInfra,
 user_id?: string
) => {
 try {
  const res = await post<DefaultApiResponse>(
   `/market-linkage/v1/infra`,
   {
    ...data,
    ...(user_id && {user_id}),
   }
  )
  if (res.success) {
   toast.success(
    res.message ?? 'Infra created successfully'
   )
   return res
  }
  showErrorMsg(res.message)
  return null
 } catch (err: unknown) {
  const e = err as AxiosError
  handleRejectedErrorMsg(e)
  return null
 }
}

const updateInfra = async (
 data: IInfra,
 user_id?: string
) => {
 try {
  const payload: IInfraUpdateRequest = {
   ...(user_id && {user_id: Number(user_id)}),
   infra_data: [data],
  }
  const res = await put<DefaultApiResponse>(
   `/market-linkage/v1/infra/update`,
   {...payload}
  )
  if (res.success) {
   toast.success(
    res.message ?? 'Infra updated successfully'
   )
   return res
  }
  showErrorMsg(res.message)
  return null
 } catch (err: unknown) {
  const e = err as AxiosError
  handleRejectedErrorMsg(e)
  return null
 }
}

const deleteInfra = async (
 payload: IInfraDeleteRequest
) => {
 try {
  const res = await remove<DefaultApiResponse>(
   `/market-linkage/v1/infra/update`,
   {...payload}
  )
  if (res?.success) {
   toast.success(
    res.message ?? 'Infra deleted successfully'
   )
   return res
  }
  showErrorMsg(res.message)
  return null
 } catch (err: unknown) {
  const e = err as AxiosError
  handleRejectedErrorMsg(e)
  return null
 }
}

export {
 fetchParentOnboardingItmesConfig,
 updateOnboardingItmes,
 fetchChildOnboardingItmesConfig,
 fetchProductCategoryLicense,
 fetchProductCategory,
 addProductCategoryLicense,
 updateProductCategoryLicense,
 deleteProductCategoryLicense,
 fetchOrganisationMapping,
 fetchOrganisationMappingCounts,
 removeOrganisationMapping,
 createOrganisationMapping,
 fetchOrganisation,
 fetchInfraList,
 fetchInfraTypes,
 createInfra,
 updateInfra,
 deleteInfra,
}
